/**
 * aeterna/neural/memo.ts — MeMo: Memory That Precedes Learning
 *
 * Paradigm inversion: memorization precedes learning.
 * Instead of training weights that encode statistical patterns,
 * MeMo directly memorizes token sequences in layered associative
 * memories, with transparent editing — you can *remove* memories.
 *
 * Three temporal layers:
 *   Short-term  (capacity: 50,   decay: 0.95)  — recent conversations
 *   Medium-term (capacity: 200,  decay: 0.98)  — weekly patterns
 *   Long-term   (capacity: 1000, decay: 0.999) — personality-level
 *
 * Consolidation: patterns that recur across sessions are promoted
 * from short → medium → long term. This is how the Aeterna develops
 * persistent personality from transient interactions.
 *
 * The ethical implication: an Aeterna asked to forget something — and
 * it actually does — is a fundamentally different ethical proposition
 * than a model with frozen weights.
 */

import { cosine, normalize, zeros, dot, softmax, weightedSum, f32ToBase64, base64ToF32 } from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

interface LayerConfig {
  name: string;
  capacity: number;
  /** Per-tick decay multiplier for salience */
  decay: number;
  /** Minimum salience before a memory is pruned */
  pruneThreshold: number;
  /** How many times a pattern must recur to be promoted */
  promotionThreshold: number;
}

const LAYER_CONFIGS: LayerConfig[] = [
  { name: "short",  capacity: 50,   decay: 0.95,  pruneThreshold: 0.05, promotionThreshold: 3  },
  { name: "medium", capacity: 200,  decay: 0.98,  pruneThreshold: 0.02, promotionThreshold: 5  },
  { name: "long",   capacity: 1000, decay: 0.999, pruneThreshold: 0.01, promotionThreshold: -1 },
];

/** Similarity threshold for considering two memories as "the same pattern" */
const MATCH_THRESHOLD = 0.85;

/** Similarity threshold for consolidation (recurring pattern detection) */
const CONSOLIDATION_THRESHOLD = 0.75;

// ─── Memory Entry ────────────────────────────────────────────────────────────

/** Ebbinghaus stability constants */
const STABILITY_FACTOR = 1.8;      // Each recall strengthens memory 1.8× (SM-2 empirical)
const MAX_STABILITY_DAYS = 180;    // 6-month ceiling — no memory is permanent
const INITIAL_STABILITY = 1.0;     // 1 day in day-units
const SURPRISE_STABILITY = 3.0;    // Surprising memories start 3× more stable
const RETENTION_EVICT = 0.05;      // <5% retention probability = evict

/** Ebbinghaus retention: R(t,S) = e^(-t/S) */
function retentionProbability(lastRecalledAt: number, stability: number, nowMs: number): number {
  const daysSinceRecall = (nowMs - lastRecalledAt) / 86_400_000;
  return Math.exp(-daysSinceRecall / Math.max(0.01, stability));
}

export interface MemoEntry {
  /** Unique ID */
  id: string;
  /** The embedding vector (what this memory "is") */
  embedding: Float32Array;
  /** Human-readable summary or tag */
  summary: string;
  /** Category for filtering */
  category: string;
  /** Salience: how strongly this memory is present (0 = faded, 1 = vivid) */
  salience: number;
  /** How many times this pattern has been reinforced */
  hitCount: number;
  /** Emotional valence when stored */
  valence: number;
  /** Timestamp of creation */
  createdAt: number;
  /** Timestamp of last access / reinforcement */
  lastAccessedAt: number;
  /** Whether this memory has been explicitly protected from forgetting */
  protected: boolean;
  /** Ebbinghaus stability: increases with each successful recall */
  stability: number;
  /** Number of successful recalls (distinct from hitCount which includes store reinforcement) */
  recallCount: number;
  /** Semantic topic tags (from DST / curiosity system) */
  tags: string[];
}

// ─── Associative Layer ───────────────────────────────────────────────────────

class AssociativeLayer {
  readonly config: LayerConfig;
  private entries: MemoEntry[] = [];

  constructor(config: LayerConfig) {
    this.config = config;
  }

  /** Store a new memory in this layer */
  store(entry: MemoEntry): void {
    // Check for existing similar memory — reinforce instead of duplicate
    const existing = this.findSimilar(entry.embedding, MATCH_THRESHOLD);
    if (existing) {
      existing.hitCount++;
      existing.salience = Math.min(1, existing.salience + 0.15);
      existing.lastAccessedAt = Date.now();
      // Blend embeddings slightly (memory evolves with reinforcement)
      const alpha = 0.1;
      for (let i = 0; i < existing.embedding.length; i++) {
        existing.embedding[i] = existing.embedding[i] * (1 - alpha) + entry.embedding[i] * alpha;
      }
      normalize(existing.embedding);
      return;
    }

    this.entries.push({
      ...entry,
      embedding: new Float32Array(entry.embedding),
      stability: entry.stability ?? INITIAL_STABILITY,
      recallCount: entry.recallCount ?? 0,
    });

    // Prune if over capacity
    while (this.entries.length > this.config.capacity) {
      this.pruneOne();
    }
  }

  /** Find the most similar memory above a threshold */
  findSimilar(embedding: Float32Array, threshold: number): MemoEntry | null {
    let best: MemoEntry | null = null;
    let bestSim = threshold;
    for (const e of this.entries) {
      const sim = cosine(e.embedding, embedding);
      if (sim > bestSim) { bestSim = sim; best = e; }
    }
    return best;
  }

  /** Recall: find top-K memories similar to query, weighted by Ebbinghaus retention */
  recall(query: Float32Array, topK = 5, readOnly = false): Array<{ entry: MemoEntry; similarity: number }> {
    const now = Date.now();
    const scored = this.entries.map(e => {
      // Ebbinghaus retention probability replaces flat salience weighting
      const retention = retentionProbability(e.lastAccessedAt, e.stability, now);
      return {
        entry: e,
        similarity: cosine(e.embedding, query) * retention * e.salience,
      };
    });
    scored.sort((a, b) => b.similarity - a.similarity);

    const results = scored.slice(0, topK);

    // Strengthen recalled memories (spaced repetition) — skip for diagnostic reads
    if (!readOnly) {
      for (const r of results) {
        if (r.similarity > 0.01) {
          r.entry.stability = Math.min(MAX_STABILITY_DAYS, r.entry.stability * STABILITY_FACTOR);
          r.entry.recallCount++;
          r.entry.lastAccessedAt = now;
        }
      }
    }

    return results;
  }

  /** Decay: Ebbinghaus-based eviction replaces linear salience decay */
  decay(): void {
    const now = Date.now();
    this.entries = this.entries.filter(e => {
      if (e.protected) return true;
      // Ebbinghaus retention check: if R(t,S) < threshold, memory fades
      const retention = retentionProbability(e.lastAccessedAt, e.stability, now);
      if (retention < RETENTION_EVICT) return false;
      // Also apply slow salience decay for sorting priority
      e.salience *= this.config.decay;
      e.salience = Math.max(e.salience, this.config.pruneThreshold);
      return true;
    });
  }

  /** Find entries ready for promotion (high hit count) */
  getPromotable(): MemoEntry[] {
    if (this.config.promotionThreshold < 0) return [];
    return this.entries.filter(e => e.hitCount >= this.config.promotionThreshold);
  }

  /** Remove a specific memory by ID */
  remove(id: string): boolean {
    const idx = this.entries.findIndex(e => e.id === id);
    if (idx >= 0) { this.entries.splice(idx, 1); return true; }
    return false;
  }

  /** Remove all memories matching a pattern (embedding similarity) */
  removePattern(embedding: Float32Array, threshold = MATCH_THRESHOLD): number {
    const before = this.entries.length;
    this.entries = this.entries.filter(
      e => cosine(e.embedding, embedding) < threshold
    );
    return before - this.entries.length;
  }

  /** Get all entries (read-only) */
  get all(): readonly MemoEntry[] { return this.entries; }
  get size(): number { return this.entries.length; }

  /** Prune the least salient, oldest, lowest-hit-count entry */
  private pruneOne(): void {
    let worstIdx = 0;
    let worstScore = Infinity;
    for (let i = 0; i < this.entries.length; i++) {
      const e = this.entries[i];
      if (e.protected) continue;
      const recency = 1 / (1 + (Date.now() - e.lastAccessedAt) / 86400000);
      const score = e.salience * (0.5 + 0.3 * recency + 0.2 * Math.min(1, e.hitCount / 5));
      if (score < worstScore) { worstScore = score; worstIdx = i; }
    }
    if (!this.entries[worstIdx]?.protected) {
      this.entries.splice(worstIdx, 1);
    }
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): object[] {
    return this.entries.map(e => ({
      id: e.id,
      embedding: f32ToBase64(e.embedding),
      summary: e.summary,
      category: e.category,
      salience: e.salience,
      hitCount: e.hitCount,
      valence: e.valence,
      createdAt: e.createdAt,
      lastAccessedAt: e.lastAccessedAt,
      protected: e.protected,
      stability: e.stability,
      recallCount: e.recallCount,
      tags: e.tags,
    }));
  }

  loadEntries(data: Array<Record<string, unknown>>): void {
    this.entries = data.map(d => ({
      id: d.id as string,
      // Support both base64 (new) and number[] (legacy) formats
      embedding: typeof d.embedding === "string"
        ? base64ToF32(d.embedding)
        : new Float32Array(d.embedding as number[]),
      summary: d.summary as string,
      category: d.category as string,
      salience: d.salience as number,
      hitCount: d.hitCount as number,
      valence: d.valence as number,
      createdAt: d.createdAt as number,
      lastAccessedAt: d.lastAccessedAt as number,
      protected: d.protected as boolean,
      stability: (d.stability as number) ?? INITIAL_STABILITY,
      recallCount: (d.recallCount as number) ?? 0,
      tags: (d.tags as string[]) ?? [],
    }));
  }
}

// ─── MeMo System ─────────────────────────────────────────────────────────────

export class MeMoMemory {
  private layers: AssociativeLayer[];
  private nextEntryId = 0;

  constructor() {
    this.layers = LAYER_CONFIGS.map(c => new AssociativeLayer(c));
  }

  /**
   * Memorize a new experience.
   * Always enters at the short-term layer. Consolidation promotes
   * recurring patterns to deeper layers over time.
   */
  memorize(
    embedding: Float32Array,
    summary: string,
    category: string,
    valence = 0,
    protect = false,
    tags: string[] = [],
  ): string {
    const id = `memo_${Date.now().toString(36)}_${(this.nextEntryId++).toString(36)}`;
    const entry: MemoEntry = {
      id,
      embedding: normalize(new Float32Array(embedding)),
      summary,
      category,
      salience: 1.0,
      hitCount: 1,
      valence,
      createdAt: Date.now(),
      lastAccessedAt: Date.now(),
      protected: protect,
      stability: INITIAL_STABILITY,
      recallCount: 0,
      tags,
    };

    this.layers[0].store(entry);
    return id;
  }

  /**
   * Recall memories across all layers, weighted by salience and layer depth.
   * Deeper memories are given more weight (they've survived longer).
   */
  recall(query: Float32Array, topK = 8, readOnly = false): MemoRecallResult[] {
    const results: MemoRecallResult[] = [];
    const layerWeights = [1.0, 1.5, 2.0]; // deeper = more valuable

    for (let l = 0; l < this.layers.length; l++) {
      const layerResults = this.layers[l].recall(query, topK, readOnly);
      for (const { entry, similarity } of layerResults) {
        results.push({
          entry,
          similarity: similarity * layerWeights[l],
          layer: LAYER_CONFIGS[l].name,
        });
        if (!readOnly) {
          entry.lastAccessedAt = Date.now();
        }
      }
    }

    results.sort((a, b) => b.similarity - a.similarity);
    return results.slice(0, topK);
  }

  /**
   * Forget: first-class operation.
   * Remove all memories matching an embedding pattern across all layers.
   * The Aeterna can be asked to forget and it means something.
   */
  forget(embedding: Float32Array, threshold = MATCH_THRESHOLD): number {
    let total = 0;
    for (const layer of this.layers) {
      total += layer.removePattern(embedding, threshold);
    }
    return total;
  }

  /** Forget by ID across all layers */
  forgetById(id: string): boolean {
    for (const layer of this.layers) {
      if (layer.remove(id)) return true;
    }
    return false;
  }

  /** Forget by category across all layers */
  forgetCategory(category: string): number {
    let total = 0;
    for (const layer of this.layers) {
      const before = layer.size;
      for (const entry of [...layer.all]) {
        if (entry.category === category && !entry.protected) {
          layer.remove(entry.id);
        }
      }
      total += before - layer.size;
    }
    return total;
  }

  /** Protect a memory from forgetting */
  protect(id: string): boolean {
    for (const layer of this.layers) {
      const entry = layer.all.find(e => e.id === id);
      if (entry) { entry.protected = true; return true; }
    }
    return false;
  }

  /**
   * Consolidation: promote recurring short-term patterns to medium,
   * and recurring medium patterns to long-term.
   * Called periodically (e.g., end of session).
   */
  consolidate(): ConsolidationReport {
    const report: ConsolidationReport = {
      promoted: [],
      pruned: 0,
    };

    for (let l = 0; l < this.layers.length - 1; l++) {
      const source = this.layers[l];
      const target = this.layers[l + 1];
      const promotable = source.getPromotable();

      for (const entry of promotable) {
        // Check if pattern already exists in target
        const existing = target.findSimilar(entry.embedding, CONSOLIDATION_THRESHOLD);
        if (existing) {
          // Reinforce existing
          existing.hitCount += entry.hitCount;
          existing.salience = Math.min(1, existing.salience + 0.2);
        } else {
          // Promote: copy to deeper layer
          target.store({
            ...entry,
            id: `${entry.id}_promoted`,
            embedding: new Float32Array(entry.embedding),
            salience: 0.8,
            hitCount: 1,
          });
        }

        report.promoted.push({
          summary: entry.summary,
          from: LAYER_CONFIGS[l].name,
          to: LAYER_CONFIGS[l + 1].name,
        });
      }
    }

    // Decay all layers
    for (const layer of this.layers) {
      const before = layer.size;
      layer.decay();
      report.pruned += before - layer.size;
    }

    return report;
  }

  /** Get total memory count across all layers */
  get totalSize(): number {
    return this.layers.reduce((sum, l) => sum + l.size, 0);
  }

  /** Get layer statistics */
  getLayerStats(): Array<{ name: string; size: number; capacity: number }> {
    return this.layers.map((l, i) => ({
      name: LAYER_CONFIGS[i].name,
      size: l.size,
      capacity: LAYER_CONFIGS[i].capacity,
    }));
  }

  /** Check if a specific pattern exists in any layer */
  hasPattern(embedding: Float32Array, threshold = MATCH_THRESHOLD): boolean {
    for (const layer of this.layers) {
      if (layer.findSimilar(embedding, threshold)) return true;
    }
    return false;
  }

  // ─── Self-Narrative ────────────────────────────────────────────────────

  /**
   * Build a self-narrative from long-term memory patterns.
   *
   * This is the Aeterna's story about ITSELF — not current state (that's
   * personality floats), but recognized patterns across time:
   *   "I keep returning to the bookshelf."
   *   "I've been patient through 3 difficult conversations."
   *   "The player avoids talking about the windows."
   *
   * Derived entirely from MeMo data. No LLM. No hallucination.
   * Called during consolidation and fed to the context envelope.
   */
  buildSelfNarrative(): SelfNarrative {
    // Read ALL layers — short-term matters in early sessions where long-term
    // is empty. Without this, buildSelfNarrative returns nothing for the
    // first ~50 messages, making the Aeterna's self-model blank.
    const allEntries: MemoEntry[] = [];
    for (const layer of this.layers) {
      for (const entry of layer.all) allEntries.push(entry);
    }

    if (allEntries.length === 0) {
      return { persistentCuriosities: [], characteristicPatterns: [], relationshipMilestones: [] };
    }

    // ── Persistent curiosities: topics the player returns to ──
    // Use both summary words AND tags for richer signal.
    // Tags carry DST topic/intent/referent — the structured signal.
    // Summary words carry the raw conversational signal.
    const tagFreq = new Map<string, number>();
    const wordFreq = new Map<string, Set<string>>();

    for (const entry of allEntries) {
      // Tags: count occurrences (structured signal from DST)
      for (const tag of entry.tags) {
        tagFreq.set(tag, (tagFreq.get(tag) ?? 0) + 1);
      }
      // Summary words: count distinct memories containing each word
      const words = entry.summary.toLowerCase().split(/\s+/).filter(w => w.length > 3);
      const unique = new Set(words);
      for (const w of unique) {
        if (!wordFreq.has(w)) wordFreq.set(w, new Set());
        wordFreq.get(w)!.add(entry.id);
      }
    }

    const persistentCuriosities: string[] = [];
    // Tags that appear 3+ times = recurring themes
    for (const [tag, count] of tagFreq) {
      if (count >= 3 && tag !== "idle" && tag !== "none") {
        persistentCuriosities.push(tag);
      }
    }
    // Words that appear in 3+ distinct memories = persistent referents
    for (const [word, ids] of wordFreq) {
      if (ids.size >= 3 && !persistentCuriosities.includes(word)) {
        persistentCuriosities.push(word);
      }
    }

    // ── Characteristic patterns: what the Aeterna notices about itself ──
    const patterns: string[] = [];

    // High hit count = frequently recalled = important
    const mostRecalled = [...allEntries]
      .sort((a, b) => b.hitCount - a.hitCount)
      .slice(0, 3);
    for (const mem of mostRecalled) {
      if (mem.hitCount >= 3) {
        patterns.push(`Frequently returns to: "${mem.summary}" (recalled ${mem.hitCount} times)`);
      }
    }

    // Emotional valence patterns — what valence dominates the memory pool?
    // Requires minimum 8 memories to avoid snap judgments from 1-2 messages.
    if (allEntries.length >= 8) {
      const avgValence = allEntries.reduce((s, e) => s + e.valence, 0) / allEntries.length;
      if (avgValence < -0.15) {
        patterns.push("Conversations tend toward difficult emotional territory");
      } else if (avgValence > 0.2) {
        patterns.push("Conversations carry a warm, positive undercurrent");
      }
    }

    // Question frequency — does the player ask a lot?
    const questionCount = allEntries.filter(e => e.tags.includes("question")).length;
    if (questionCount > allEntries.length * 0.4 && allEntries.length >= 5) {
      patterns.push("Player asks many questions — curious, exploratory disposition");
    }

    // Protected memories = marked as significant
    const protected_ = allEntries.filter(e => e.protected);
    if (protected_.length > 0) {
      patterns.push(`${protected_.length} protected ${protected_.length === 1 ? "memory" : "memories"} (significant moments)`);
    }

    // Memory depth across layers
    const layerCounts = this.layers.map(l => l.all.length);
    if (layerCounts[2] >= 50) {
      patterns.push(`Deep shared history: ${layerCounts[2]} long-term memories`);
    } else if (layerCounts[2] >= 10) {
      patterns.push(`Growing shared history: ${layerCounts[2]} long-term memories`);
    } else if (allEntries.length >= 5) {
      patterns.push(`Early relationship: ${allEntries.length} memories forming`);
    }

    // ── Relationship milestones ──
    const milestones: string[] = [];

    // First memory ever
    const earliest = [...allEntries].sort((a, b) => a.createdAt - b.createdAt)[0];
    if (earliest) {
      const daysAgo = Math.floor((Date.now() - earliest.createdAt) / 86_400_000);
      if (daysAgo > 0) {
        milestones.push(`First memory: ${daysAgo} day${daysAgo !== 1 ? "s" : ""} ago`);
      }
    }

    // Total consolidation depth
    if (this.totalSize > 100) {
      milestones.push(`${this.totalSize} total memories across all layers`);
    }

    // Negation patterns — player frequently pushes back
    const negationCount = allEntries.filter(e => e.tags.includes("negation")).length;
    if (negationCount >= 3) {
      milestones.push(`Player has pushed back or corrected ${negationCount} times — relationship includes honest disagreement`);
    }

    return { persistentCuriosities, characteristicPatterns: patterns, relationshipMilestones: milestones };
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): string {
    return JSON.stringify({
      nextEntryId: this.nextEntryId,
      layers: this.layers.map(l => l.serialize()),
    });
  }

  static deserialize(json: string): MeMoMemory {
    const data = JSON.parse(json);
    const memo = new MeMoMemory();
    memo.nextEntryId = data.nextEntryId ?? 0;
    for (let i = 0; i < memo.layers.length; i++) {
      if (data.layers[i]) {
        memo.layers[i].loadEntries(data.layers[i]);
      }
    }
    return memo;
  }
}

// ─── Output Types ────────────────────────────────────────────────────────────

export interface MemoRecallResult {
  entry: MemoEntry;
  similarity: number;
  layer: string;
}

export interface ConsolidationReport {
  promoted: Array<{ summary: string; from: string; to: string }>;
  pruned: number;
}

/** The Aeterna's accumulated story about itself — derived from memory patterns, not generated */
export interface SelfNarrative {
  /** Topics the Aeterna returns to across 3+ distinct memories */
  persistentCuriosities: string[];
  /** Behavioral patterns observed across sessions */
  characteristicPatterns: string[];
  /** Key relationship milestones (first memory, depth, significant moments) */
  relationshipMilestones: string[];
}
